import React, { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Character name
const CHARACTER_NAME = "小酷";

// Badge definitions
const BADGES = {
  diamond: { id: "diamond", name: "钻石大脑", icon: "💎", color: "#4AEDD9", description: "不用提示就解决了问题！" },
  gold: { id: "gold", name: "金矿达人", icon: "🥇", color: "#FFD700", description: "只用1个提示就解决了问题！" },
  iron: { id: "iron", name: "铁血意志", icon: "🔶", color: "#C0C0C0", description: "用2个提示解决了问题！" },
  stone: { id: "stone", name: "石头起步", icon: "🪨", color: "#7F7F7F", description: "坚持到底，解决了问题！" }
};

// Get badge type based on hint count
const getBadgeType = (hintCount) => {
  if (hintCount === 0) return BADGES.diamond;
  if (hintCount === 1) return BADGES.gold;
  if (hintCount === 2) return BADGES.iron;
  return BADGES.stone;
};

// Minecraft-themed Subject icons
const SUBJECTS = [
  { id: "math", name: "数学", icon: "⛏️", color: "#5D8A3E", bgColor: "#3D5A2E" },
  { id: "english", name: "英语", icon: "📜", color: "#4AEDD9", bgColor: "#2A9D8F" },
  { id: "chinese", name: "语文", icon: "📕", color: "#9C6B30", bgColor: "#6B4423" },
  { id: "science", name: "科学", icon: "🧪", color: "#FFD700", bgColor: "#CCA800" },
];

// Xiao Ku Avatar Component
const XiaoKuAvatar = ({ size = 45 }) => (
  <div 
    className="xiaoku-avatar" 
    style={{ width: size, height: size }}
    title={CHARACTER_NAME}
  >
    <svg viewBox="0 0 16 16" style={{ width: '100%', height: '100%' }}>
      {/* Face background */}
      <rect x="2" y="2" width="12" height="12" fill="#C19A6B"/>
      {/* Hair */}
      <rect x="2" y="2" width="12" height="4" fill="#4A3728"/>
      <rect x="2" y="2" width="3" height="6" fill="#4A3728"/>
      <rect x="11" y="2" width="3" height="6" fill="#4A3728"/>
      {/* Eyes */}
      <rect x="4" y="7" width="2" height="2" fill="#1D1D21"/>
      <rect x="10" y="7" width="2" height="2" fill="#1D1D21"/>
      {/* Eye shine */}
      <rect x="4" y="7" width="1" height="1" fill="#FFFFFF"/>
      <rect x="10" y="7" width="1" height="1" fill="#FFFFFF"/>
      {/* Smile */}
      <rect x="6" y="11" width="4" height="1" fill="#8B4513"/>
      {/* Cheeks */}
      <rect x="3" y="9" width="2" height="1" fill="#E8B4B4"/>
      <rect x="11" y="9" width="2" height="1" fill="#E8B4B4"/>
    </svg>
  </div>
);

// Badge Popup Component - Shows when badge is earned
const BadgePopup = ({ badge, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  if (!badge) return null;

  return (
    <div className="badge-popup-overlay" onClick={onClose}>
      <div className="badge-popup" data-testid="badge-popup">
        <div className="badge-popup-glow" style={{ backgroundColor: badge.color }}></div>
        <div className="badge-popup-content">
          <div className="badge-popup-title">🎉 获得成就！</div>
          <div className="badge-popup-icon" style={{ color: badge.color }}>
            {badge.icon}
          </div>
          <div className="badge-popup-name" style={{ color: badge.color }}>
            {badge.name}
          </div>
          <div className="badge-popup-description">
            {badge.description}
          </div>
          <div className="badge-popup-hint-info">
            使用了 {badge.hintCount} 个提示
          </div>
        </div>
      </div>
    </div>
  );
};

// Badge Counter Component - Shows in header
const BadgeCounter = ({ counts, total, onClick }) => {
  return (
    <button className="badge-counter" onClick={onClick} data-testid="badge-counter">
      <span className="badge-counter-icon">🏆</span>
      <span className="badge-counter-total">{total}</span>
      <div className="badge-counter-breakdown">
        <span title="钻石大脑">💎{counts.diamond}</span>
        <span title="金矿达人">🥇{counts.gold}</span>
        <span title="铁血意志">🔶{counts.iron}</span>
        <span title="石头起步">🪨{counts.stone}</span>
      </div>
    </button>
  );
};

// Speech synthesis hook
const useSpeechSynthesis = () => {
  const [speaking, setSpeaking] = useState(false);
  const [supported, setSupported] = useState(false);
  const [voicesLoaded, setVoicesLoaded] = useState(false);

  useEffect(() => {
    if ('speechSynthesis' in window) {
      setSupported(true);
      
      // Load voices
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          setVoicesLoaded(true);
        }
      };
      
      loadVoices();
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);

  const speak = useCallback((text) => {
    if (!supported) return;
    
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'zh-CN';
    
    // Voice settings optimized for 10-12 year old children
    utterance.rate = 0.85;      // Slightly slower for better comprehension
    utterance.pitch = 1.3;      // Higher pitch for friendly, youthful sound
    utterance.volume = 1.0;     // Full volume
    
    // Try to find a child-friendly voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoices = voices.filter(v => 
      v.lang.includes('zh') && (
        v.name.toLowerCase().includes('female') ||
        v.name.toLowerCase().includes('xiaoxiao') ||
        v.name.toLowerCase().includes('xiaoyi') ||
        v.name.includes('小') ||
        v.name.includes('女')
      )
    );
    
    if (preferredVoices.length > 0) {
      utterance.voice = preferredVoices[0];
    } else {
      // Fallback to any Chinese voice
      const chineseVoice = voices.find(v => v.lang.includes('zh'));
      if (chineseVoice) {
        utterance.voice = chineseVoice;
      }
    }
    
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    
    window.speechSynthesis.speak(utterance);
  }, [supported]);

  const stop = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [supported]);

  return { speak, stop, speaking, supported, voicesLoaded };
};

// Speech recognition hook
const useSpeechRecognition = () => {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [supported, setSupported] = useState(false);
  const recognitionRef = useRef(null);
  const isStartingRef = useRef(false);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSupported(true);
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'zh-CN';

      recognitionRef.current.onresult = (event) => {
        const current = event.resultIndex;
        const result = event.results[current];
        setTranscript(result[0].transcript);
      };

      recognitionRef.current.onend = () => {
        setListening(false);
        isStartingRef.current = false;
      };

      recognitionRef.current.onerror = (event) => {
        console.log('Speech recognition error:', event.error);
        setListening(false);
        isStartingRef.current = false;
      };
    }
  }, []);

  const startListening = useCallback(() => {
    if (!supported || !recognitionRef.current || isStartingRef.current) return;
    try {
      isStartingRef.current = true;
      setTranscript('');
      recognitionRef.current.start();
      setListening(true);
    } catch (error) {
      console.log('Start listening error:', error);
      isStartingRef.current = false;
      setListening(false);
    }
  }, [supported]);

  const stopListening = useCallback(() => {
    if (!supported || !recognitionRef.current) return;
    try {
      recognitionRef.current.stop();
    } catch (error) {
      console.log('Stop listening error:', error);
    }
    setListening(false);
    isStartingRef.current = false;
  }, [supported]);

  return { startListening, stopListening, listening, transcript, supported, setTranscript };
};

// Helper function to check if message contains a question for the student
const containsQuestion = (content) => {
  if (!content) return false;
  // Check for Chinese question marks or common question patterns
  const questionPatterns = [
    '？', '?',
    '你觉得', '你认为', '你能', '你会', '你知道',
    '怎么', '如何', '什么', '为什么', '哪个', '哪些',
    '试试', '想想', '算算', '说说',
    '第一步', '下一步', '接下来'
  ];
  return questionPatterns.some(pattern => content.includes(pattern));
};

// Message component
const Message = ({ message, onSpeak, speaking, onRequestHint, hintLoading, currentHintCount }) => {
  const isUser = message.role === 'user';
  const showHintButton = !isUser && containsQuestion(message.content) && !message.isHint;
  
  return (
    <div className={`message-container ${isUser ? 'user' : 'assistant'} ${message.isHint ? 'hint-message' : ''}`} data-testid={`message-${message.id}`}>
      <div className={`message-bubble ${isUser ? 'user-bubble' : 'assistant-bubble'} ${message.isHint ? 'hint-bubble' : ''}`}>
        {!isUser && (
          <XiaoKuAvatar size={45} />
        )}
        <div className="message-content">
          {isUser && message.isVoice && <span className="voice-badge">🎤</span>}
          {isUser && message.hasImage && <span className="voice-badge">📷</span>}
          {message.isHint && <span className="hint-badge">💡 提示 {message.hintNumber}</span>}
          {message.hasImage && message.imagePreview && (
            <img 
              src={message.imagePreview} 
              alt="上传的图片" 
              className="message-image"
            />
          )}
          <p>{message.content}</p>
          <div className="message-actions">
            {!isUser && (
              <button 
                className="speak-btn"
                onClick={() => onSpeak(message.content)}
                disabled={speaking}
                data-testid={`speak-btn-${message.id}`}
              >
                {speaking ? '🔊' : '🔈'}
              </button>
            )}
            {showHintButton && (
              <button 
                className="hint-btn"
                onClick={() => onRequestHint(message.content)}
                disabled={hintLoading}
                data-testid={`hint-btn-${message.id}`}
                title="获取提示"
              >
                {hintLoading ? '⏳' : '💡'}
                <span className="hint-btn-text">
                  {hintLoading ? '思考中...' : `提示${currentHintCount > 0 ? ` (${currentHintCount})` : ''}`}
                </span>
              </button>
            )}
          </div>
        </div>
        {isUser && (
          <div className="message-avatar user-avatar">
            ⛏️
          </div>
        )}
      </div>
    </div>
  );
};

// Subject selector component
const SubjectSelector = ({ subjects, selected, onSelect }) => {
  return (
    <div className="subject-selector" data-testid="subject-selector">
      {subjects.map((subject) => (
        <button
          key={subject.id}
          className={`subject-btn ${selected === subject.id ? 'selected' : ''}`}
          style={{
            backgroundColor: selected === subject.id ? subject.color : subject.bgColor,
            color: selected === subject.id ? 'white' : subject.color,
            borderColor: subject.color
          }}
          onClick={() => onSelect(subject.id)}
          data-testid={`subject-${subject.id}`}
        >
          <span className="subject-icon">{subject.icon}</span>
          <span className="subject-name">{subject.name}</span>
        </button>
      ))}
    </div>
  );
};

// Main App component
function App() {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('math');
  const [sessionId, setSessionId] = useState('');
  const [isVoiceInput, setIsVoiceInput] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [hintLoading, setHintLoading] = useState(false);
  const [hintCount, setHintCount] = useState(0);
  const [lastQuestionContext, setLastQuestionContext] = useState('');
  const [pendingBadge, setPendingBadge] = useState(false); // Track if we should award badge on next response
  const [earnedBadge, setEarnedBadge] = useState(null); // Badge popup data
  const [badgeCounts, setBadgeCounts] = useState({ diamond: 0, gold: 0, iron: 0, stone: 0 });
  const [totalBadges, setTotalBadges] = useState(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const { speak, stop, speaking, supported: ttsSupported } = useSpeechSynthesis();
  const { startListening, stopListening, listening, transcript, supported: sttSupported, setTranscript } = useSpeechRecognition();

  // Load badges on session init
  const loadBadges = useCallback(async (sid) => {
    try {
      const response = await axios.get(`${API}/badges/${sid}`);
      setBadgeCounts(response.data.counts);
      setTotalBadges(response.data.total);
    } catch (error) {
      console.error('Failed to load badges:', error);
    }
  }, []);

  // Initialize session
  useEffect(() => {
    const storedSessionId = localStorage.getItem('study_session_id');
    if (storedSessionId) {
      setSessionId(storedSessionId);
      loadBadges(storedSessionId);
    } else {
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('study_session_id', newSessionId);
      setSessionId(newSessionId);
    }
  }, [loadBadges]);

  // Load chat history when subject changes
  useEffect(() => {
    if (sessionId) {
      loadChatHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, selectedSubject]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Update input when speech recognition gets result
  useEffect(() => {
    if (transcript) {
      setInputText(transcript);
      setIsVoiceInput(true); // Mark as voice input
    }
  }, [transcript]);

  const loadChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history/${sessionId}?subject=${selectedSubject}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const sendMessage = async (forceVoiceMode) => {
    if (!inputText.trim() || loading) return;

    // Ensure forceVoiceMode is a boolean, not an event object
    const useVoiceMode = forceVoiceMode === true || isVoiceInput;
    
    // Check if this is a response to a question (pendingBadge is set)
    const shouldAwardBadge = pendingBadge;
    const currentHintCount = hintCount;
    
    // Reset hint count when user sends a new message
    resetHintCount();
    setPendingBadge(false);
    
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputText.trim(),
      timestamp: new Date().toISOString(),
      isVoice: useVoiceMode
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setTranscript('');
    setIsVoiceInput(false); // Reset voice input flag
    setLoading(true);

    // Award badge if responding to a question
    if (shouldAwardBadge) {
      awardBadge(currentHintCount);
    }

    try {
      const response = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: userMessage.content,
        subject: selectedSubject,
        voice_mode: Boolean(useVoiceMode)
      });

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Check if the new response contains a question - set pending badge
      if (containsQuestion(response.data.response)) {
        setPendingBadge(true);
      }
      
      // Auto-speak response if voice mode was used or TTS is supported
      if (ttsSupported && useVoiceMode) {
        speak(response.data.response);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: '哎呀，出了点小问题。请再试一次吧！🙏',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Request a hint for the current question
  const requestHint = async (questionContent) => {
    if (hintLoading) return;
    
    setHintLoading(true);
    const newHintCount = hintCount + 1;
    
    try {
      // Get student's recent messages as context
      const recentUserMessages = messages
        .filter(m => m.role === 'user')
        .slice(-3)
        .map(m => m.content)
        .join(' | ');
      
      const response = await axios.post(`${API}/chat/hint`, {
        session_id: sessionId,
        subject: selectedSubject,
        original_question: questionContent,
        student_context: recentUserMessages,
        hint_count: newHintCount
      });

      const hintMessage = {
        id: `hint_${Date.now()}`,
        role: 'assistant',
        content: response.data.hint,
        timestamp: new Date().toISOString(),
        isHint: true,
        hintNumber: newHintCount
      };

      setMessages(prev => [...prev, hintMessage]);
      setHintCount(newHintCount);
      setLastQuestionContext(questionContent);
      
      // Speak the hint if TTS is supported
      if (ttsSupported) {
        speak(response.data.hint);
      }
    } catch (error) {
      console.error('Failed to get hint:', error);
      const errorMessage = {
        id: `hint_error_${Date.now()}`,
        role: 'assistant',
        content: '抱歉，小酷暂时想不出提示，再试一次吧！',
        timestamp: new Date().toISOString(),
        isHint: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setHintLoading(false);
    }
  };

  // Reset hint count when user sends a new message
  const resetHintCount = () => {
    setHintCount(0);
    setLastQuestionContext('');
  };

  // Award badge when student responds to a question
  const awardBadge = async (currentHintCount) => {
    try {
      const response = await axios.post(`${API}/badges/award`, {
        session_id: sessionId,
        subject: selectedSubject,
        hint_count: currentHintCount
      });
      
      const badge = response.data.badge;
      setEarnedBadge({
        ...badge,
        hintCount: currentHintCount
      });
      
      // Update counts
      setBadgeCounts(prev => ({
        ...prev,
        [badge.type]: prev[badge.type] + 1
      }));
      setTotalBadges(prev => prev + 1);
    } catch (error) {
      console.error('Failed to award badge:', error);
    }
  };

  // Close badge popup
  const closeBadgePopup = () => {
    setEarnedBadge(null);
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('图片太大了，请选择小于5MB的图片');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        alert('请选择图片文件');
        return;
      }
      
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Clear selected image
  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Send message with image
  const sendImageMessage = async () => {
    if (!selectedImage || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputText.trim() || '[图片]',
      timestamp: new Date().toISOString(),
      hasImage: true,
      imagePreview: imagePreview
    };

    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    
    const messageText = inputText.trim();
    setInputText('');
    clearImage();

    try {
      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('session_id', sessionId);
      formData.append('subject', selectedSubject);
      formData.append('message', messageText);

      const response = await axios.post(`${API}/chat/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      if (ttsSupported) {
        speak(response.data.response);
      }
    } catch (error) {
      console.error('Failed to send image:', error);
      const errorMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: '图片处理出了点问题，请重新拍摄试试',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    try {
      // Stop any ongoing speech
      stop();
      // Clear messages immediately for better UX
      setMessages([]);
      clearImage();
      // Then delete from backend
      await axios.delete(`${API}/chat/history/${sessionId}?subject=${selectedSubject}`);
    } catch (error) {
      console.error('Failed to clear history:', error);
      // Still keep messages cleared even if backend fails
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (selectedImage) {
        sendImageMessage();
      } else {
        sendMessage();
      }
    }
  };

  const handleMicClick = () => {
    if (listening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const handleSendClick = () => {
    if (selectedImage) {
      sendImageMessage();
    } else {
      sendMessage();
    }
  };

  const currentSubject = SUBJECTS.find(s => s.id === selectedSubject);

  // Get a joke or fun fact
  const getJokeOrFact = async () => {
    if (loading) return;
    
    setLoading(true);
    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: '🎲 给我讲个笑话或有趣的知识！',
      timestamp: new Date().toISOString(),
      isFunRequest: true
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await axios.post(`${API}/chat/fun`, {
        session_id: sessionId,
        subject: selectedSubject
      });

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      if (ttsSupported) {
        speak(response.data.response);
      }
    } catch (error) {
      console.error('Failed to get joke:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container" data-testid="app-container">
      {/* Badge Popup */}
      {earnedBadge && (
        <BadgePopup badge={earnedBadge} onClose={closeBadgePopup} />
      )}
      
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="header-title">
            <XiaoKuAvatar size={50} />
            <h1>{CHARACTER_NAME}学习助手</h1>
          </div>
          <div className="header-subtitle">
            阿礼安的学习助手
          </div>
          {/* Badge Counter */}
          <BadgeCounter 
            counts={badgeCounts}
            total={totalBadges}
            onClick={() => {}}
          />
        </div>
      </header>

      {/* Subject Selector */}
      <SubjectSelector 
        subjects={SUBJECTS}
        selected={selectedSubject}
        onSelect={setSelectedSubject}
      />

      {/* Chat Area */}
      <main className="chat-area" data-testid="chat-area">
        {messages.length === 0 ? (
          <div className="welcome-message">
            <div className="welcome-icon">{currentSubject?.icon}</div>
            <h2>嘿！我是{CHARACTER_NAME}，阿礼安的{currentSubject?.name}助手！</h2>
            <p>准备好一起探索知识世界了吗？</p>
            <div className="tips">
              <p>⬥ 使用指南：</p>
              <ul>
                <li>输入问题或点击🎙️用语音提问</li>
                <li>点击📷上传作业图片</li>
                <li>点击🎲获取笑话或有趣知识</li>
                <li>点击🔈让{CHARACTER_NAME}读出回答</li>
                <li>点击💡获取提示（不会直接给答案哦！）</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="messages-container">
            {messages.map((msg, index) => (
              <Message 
                key={msg.id || index}
                message={msg}
                onSpeak={speak}
                speaking={speaking}
                onRequestHint={requestHint}
                hintLoading={hintLoading}
                currentHintCount={hintCount}
              />
            ))}
            {loading && (
              <div className="message-container assistant">
                <div className="message-bubble assistant-bubble">
                  <XiaoKuAvatar size={45} />
                  <div className="message-content">
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </main>

      {/* Input Area */}
      <footer className="input-area">
        <div className="input-container">
          <button 
            className="clear-btn"
            onClick={clearHistory}
            title="清除对话"
            data-testid="clear-btn"
          >
            🗑️
          </button>

          {/* Fun button for jokes and facts */}
          <button 
            className="fun-btn"
            onClick={getJokeOrFact}
            disabled={loading}
            title="笑话/趣味知识"
            data-testid="fun-btn"
          >
            🎲
          </button>
          
          <div className="input-wrapper">
            <textarea
              className="message-input"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="输入你的问题..."
              rows={1}
              data-testid="message-input"
            />
          </div>

          {sttSupported && (
            <button 
              className={`mic-btn ${listening ? 'listening' : ''}`}
              onClick={handleMicClick}
              title={listening ? '停止录音' : '开始录音'}
              data-testid="mic-btn"
            >
              {listening ? '🎤' : '🎙️'}
            </button>
          )}

          {/* Image upload button */}
          <input
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            ref={fileInputRef}
            style={{ display: 'none' }}
            data-testid="image-input"
          />
          <button 
            className="image-btn"
            onClick={() => fileInputRef.current?.click()}
            title="上传图片"
            data-testid="image-btn"
          >
            📷
          </button>

          <button 
            className="send-btn"
            onClick={handleSendClick}
            disabled={(!inputText.trim() && !selectedImage) || loading}
            data-testid="send-btn"
          >
            {loading ? '⏳' : '📤'}
          </button>
        </div>
        
        {/* Image preview */}
        {imagePreview && (
          <div className="image-preview-container">
            <img src={imagePreview} alt="预览" className="image-preview" />
            <button className="remove-image-btn" onClick={clearImage}>✕</button>
            <span className="image-hint">已选择图片，可以添加问题后发送</span>
          </div>
        )}
        
        {listening && (
          <div className="listening-indicator">
            <span className="pulse"></span>
            正在听你说话...
          </div>
        )}
      </footer>
    </div>
  );
}

export default App;
